import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { FunnelSimple, X, SlidersHorizontal } from "@phosphor-icons/react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const CAT_META = {
  road: { name: "Road Bikes", h1: "2026 Road Bikes", blurb: "Flagship carbon race machines — Specialized S-Works Tarmac SL8 & SL9, Trek Madone SLR 9 and more, in stock across the UK & EU." },
  mountain: { name: "Mountain Bikes", h1: "Mountain & XC Bikes", blurb: "From the Marlin to the S-Works Epic World Cup — the fastest XC bikes of 2026 plus hard-hitting trail and enduro machines." },
  electric: { name: "E-Bikes", h1: "Premium Carbon E-Bikes", blurb: "Premium carbon e-bikes for the EU — Turbo Levo 4, Fuel+ EX Gen 2 and featherweight e-road from Specialized & Trek." },
  gravel: { name: "Gravel Bikes", h1: "Gravel Race Bikes", blurb: "Checkmate SLR and Diverge STR — race-proven gravel bikes for European backroads and start lines." },
  framesets: { name: "Specialized Framesets", h1: "Specialized Framesets", blurb: "Official Specialized framesets — Tarmac SL9, Aethos 2, Crux, Epic and more, every chassis 25% below official RRP. Build your dream bike." },
  surron: { name: "Sur-Ron Electric Bikes", h1: "Sur-Ron Electric Bikes", blurb: "The official Sur-Ron range at UK RRP — Light Bee X, road-legal Light Bee L1E, Ultra Bee, Ultra Bee HP, Storm Bee and Hyper Bee. Genuine UK-spec bikes with official manuals and 12-month warranty." },
  components: { name: "Components & Parts", h1: "Components & Maintenance Parts", blurb: "Professional road bike maintenance parts — genuine Shimano & SRAM chains, cassettes, power cranks, pedals and frame protection. All fairly discounted 15% off RRP." },
};

const SORTS = [
  { v: "popular", l: "Most Popular" },
  { v: "price-asc", l: "Price: Low to High" },
  { v: "price-desc", l: "Price: High to Low" },
  { v: "rating", l: "Top Rated" },
  { v: "newest", l: "Newest" },
];

export default function Shop({ forceCategory }) {
  const params = useParams();
  const category = forceCategory || params.category || "all";
  const [sp, setSp] = useSearchParams();
  const search = sp.get("search") || "";
  const sort = sp.get("sort") || "popular";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxPrice, setMaxPrice] = useState(15000);
  const [showFilters, setShowFilters] = useState(false);

  const meta = CAT_META[category] || { name: "Shop", h1: search ? `Search results for "${search}"` : "The 2026 Collection", blurb: "Explore the full 2026 range — Specialized, Trek and professional-grade components, delivered across the UK & EU." };

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category !== "all") q.set("category", category);
    if (search) q.set("search", search);
    q.set("sort", sort);
    api.get(`/products?${q.toString()}`).then((r) => { setProducts(r.data); setLoading(false); });
  }, [category, search, sort]);

  const filtered = useMemo(() => products.filter((p) => (p.sale_price || p.price) <= maxPrice), [products, maxPrice]);

  const setSort = (v) => { const n = new URLSearchParams(sp); n.set("sort", v); setSp(n); };

  return (
    <>
      <SEO
        title={meta.h1}
        description={`${meta.blurb} Free UK delivery, 5-year warranty & 0% finance at Ebikes&Gear.`}
        path={category === "all" ? "/shop" : `/category/${category}`}
      />

      {/* Header band */}
      <section className="border-b border-border bg-card">
        <div className="container-px py-12 lg:py-16">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link><span>/</span>
            <Link to="/shop" className="hover:text-primary">Shop</Link>
            {category !== "all" && <><span>/</span><span className="text-foreground font-medium">{meta.name}</span></>}
          </nav>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">{meta.h1}</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{meta.blurb}</p>
        </div>
      </section>

      <section className="container-px py-10">
        {/* Category pills */}
        <div className="no-scrollbar -mx-1 mb-8 flex gap-2 overflow-x-auto px-1 pb-1">
          <Link to="/shop" className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${category === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>All</Link>
          {Object.entries(CAT_META).map(([slug, m]) => (
            <Link key={slug} to={`/category/${slug}`} className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${category === slug ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
              {m.name}
            </Link>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowFilters((s) => !s)} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary" data-testid="filter-toggle">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <span className="text-sm text-muted-foreground" data-testid="result-count">{filtered.length} products</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">Sort by</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold outline-none focus:border-primary" data-testid="sort-select">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="mt-5 rounded-2xl border border-border bg-card p-5" data-testid="filter-panel">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold">Max price: <span className="text-primary">£{maxPrice.toLocaleString()}</span></label>
              <button onClick={() => setMaxPrice(15000)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"><X size={12} /> Reset</button>
            </div>
            <input type="range" min={25} max={15000} step={25} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="mt-3 w-full accent-primary" data-testid="price-slider" />
          </div>
        )}

        {loading ? (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-3xl bg-muted" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center">
            <FunnelSimple size={56} className="mx-auto text-muted-foreground/40" />
            <p className="mt-4 text-lg font-semibold">No products match your filters</p>
            <Link to="/shop" className="btn-primary mt-5">Reset & browse all</Link>
          </div>
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="product-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.04}><ProductCard product={p} index={i} /></Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
